import { call, delay, put, select } from 'redux-saga/effects'
import get from 'lodash/get'
import { replace } from 'connected-react-router'

import { parseErrors, getOpenApiInstance, Response } from '../../api/api'
import {
  Action,
  CacheSmsSendResponse,
  IdentityStatusResponse,
  Saga,
  SendSmsInterface,
  VerifyCacheResponse,
} from '../../types'
import {
  appActions,
  appSelectors,
  clientActions,
  modalDialogActions,
  modalLoaderActions,
  productActions,
} from '../../redux'
import { fromCbl } from '../../redux/reducers/client/model'
import * as clientMappers from './client.mappers'

import createLog from '../../utils/createLog'
import {
  API_ERROR,
  LOAN_DATA_MISSING,
  MAKE_CACHE_AUTH_DIALOG,
  RESET_CONTINUE_DIALOG,
} from '../../constants/modals'
import * as formSelectors from '../formSelectors'
import { IdentityStatus, SmsVerifyResponse, POLLING_DELAY_IN_MS } from '../../constants'
import Routes from '../../constants/routes'
import { ResponseError } from '../../utils/errors'
import { localCookie } from '../../utils/localCookie'
import { getUtmParams, openLinkNewTab } from '../../utils/url'

import { genFlowId } from '../../utils/flowId'

interface DataSavingSaga {
  apiCall: () => any
  mappers: { api?: any; form: any }
}

const createDataSavingSaga = ({ apiCall, mappers }: DataSavingSaga) =>
  function* tmpOne({ payload: { success, error } }: Action<any>): Saga {
    const loader = yield put(modalLoaderActions.showModalLoader())

    const clientData = yield select(mappers.form)

    yield put(clientActions.saveClientDataToStore(clientData))

    const requestData = mappers.api ? yield select(mappers.api) : clientData

    const response = yield call(apiCall(), requestData)

    if (response.responseStatus === Response.ok) {
      success()
    } else if (response.responseStatus === Response.dataError) {
      error({
        type: response.responseStatus,
        errors: parseErrors(response),
        warnings: parseErrors(response, true),
      })
    } else {
      error({ type: response.responseStatus })
    }

    yield put(modalLoaderActions.hideModalLoader(loader.id))
  }

export const saveClientInfo = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveClientInfo,
  mappers: { form: clientMappers.getClientInfo },
})

export const saveAddresses = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveClientAddresses,
  mappers: {
    form: clientMappers.getClientAddresses,
    api: clientMappers.getClientAddressesForApi,
  },
})

export const saveClientDetails = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveClientDetails,
  mappers: { form: clientMappers.getClientDetails },
})

export const saveFinanceProfession = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveFinanceProfession,
  mappers: { form: clientMappers.getFinanceProfession },
})

export const saveFinanceEmployer = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveFinanceEmployer,
  mappers: { form: clientMappers.getFinanceEmployer },
})

export const saveFinanceIncome = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveFinanceIncome,
  mappers: { form: clientMappers.getFinanceIncome },
})

export const saveFinanceExpenses = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveFinanceExpenses,
  mappers: { form: clientMappers.getFinanceExpenses },
})

export const saveClientAdditionalInfo = createDataSavingSaga({
  apiCall: () => getOpenApiInstance().saveClientAdditionalInfo,
  mappers: {
    api: clientMappers.getClientAdditionalInfo,
    form: formSelectors.getClientAdditionalInfoForm,
  },
})

export function* requestScoringSmsCode({ payload: { error } }: Action<any>): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())
  const response = yield call(getOpenApiInstance().requestScoringSmsCode)

  if (response.responseStatus !== Response.ok) {
    error(response.responseStatus)
  }
  yield put(modalLoaderActions.hideModalLoader(loader.id))
}

export function* identitySendSms({
  payload: { success, smsSent },
}: Action<SendSmsInterface>): Saga {
  let response
  if (smsSent) {
    response = yield call(getOpenApiInstance().postMobileIdResend)
  } else {
    response = yield call(getOpenApiInstance().postMobileIdStart)
  }

  if (response.responseStatus === Response.ok) {
    success()
  } else {
    throw new ResponseError('client - identitySendSms saga', response)
  }
}

export function* startIdentityStatus(): Saga {
  const response = yield call(getOpenApiInstance().getMobileIdStatus)

  if (response.responseStatus === Response.ok) {
    const statusResponse: IdentityStatusResponse = response.data || {}
    if (statusResponse.status) {
      yield put(clientActions.saveIdentityStatusToStore(statusResponse.status))
      if (
        statusResponse.status === IdentityStatus.LINK_GENERATED ||
        statusResponse.status === IdentityStatus.LINK_OPENED
      ) {
        yield delay(POLLING_DELAY_IN_MS)
        yield put(clientActions.startIdentityStatus())
      }
    }
  } else {
    throw new ResponseError('client - startIdentityStatus saga', response)
  }
}

export function* checkExistingFlow(): Saga {
  const response = yield call(getOpenApiInstance().testCacheExists)

  if (response.responseStatus === Response.ok && response.data.exists) {
    yield put(modalDialogActions.showModalDialog(RESET_CONTINUE_DIALOG))
  } else {
    const url = new URL(window.location.href)
    const flowIdInUrl = !!url.searchParams.get('xflowid')
    if (flowIdInUrl) {
      yield put(appActions.setAppReady())
      yield put(replace(Routes.NOT_FOUND))
    } else {
      // We don't know if flow-id was used (can be locked) so we regenerate it.
      const newFlowId = genFlowId()
      yield put(appActions.setFlowId(newFlowId))
      yield put(appActions.setAppReady())
      yield call(setLoanParameters)
    }
  }
}

export function* prepareCacheAuth(): Saga {
  const response = yield call(getOpenApiInstance().requestCacheSmsCode)
  const cacheSmsSendResponse = response.data as CacheSmsSendResponse

  yield put(clientActions.saveClientDataToStore({ cacheSmsSendResponse }))

  yield put(
    modalDialogActions.showModalDialog(
      MAKE_CACHE_AUTH_DIALOG(cacheSmsSendResponse.maskedPhoneNumber)
    )
  )
}

export function* resendCacheSmsCode(): Saga {
  const response = yield call(getOpenApiInstance().requestCacheSmsCode)
  const cacheSmsSendResponse = response.data as CacheSmsSendResponse

  yield put(
    clientActions.saveClientDataToStore({ cacheSmsSendResponse, cacheVerifySmsStatus: undefined })
  )
}

export function* verifyCacheSmsCode({ payload: { success, error } }: Action<any>): Saga {
  const payload = yield select(clientMappers.getCacheAuthSmsCode)
  const response = yield call(getOpenApiInstance().verifyCacheSmsCode, payload)

  if (response.responseStatus !== Response.ok) {
    createLog({
      error: {
        name: 'verifyCacheSmsCode saga error',
        type: response.responseStatus.toString(),
        stack: response,
        message: 'API response error',
      },
    })
    return
  }

  const responseData: VerifyCacheResponse = response.data
  const cacheVerifySmsStatus: SmsVerifyResponse = responseData.status
  yield put(clientActions.saveClientDataToStore({ cacheVerifySmsStatus }))

  if (cacheVerifySmsStatus === SmsVerifyResponse.Ok) {
    const desiredStep: number = responseData.applicationProgressState
    const clientData: any = fromCbl(responseData)

    yield put(clientActions.saveClientDataToStore(clientData))
    yield put(productActions.setDataFromCache(responseData))

    // On return after scoring, the new decrypted flow ID is acquired here
    const currentFlowId = yield select(appSelectors.selectFlowId)
    const newFlowId = get(responseData, 'xflowId')
    if (newFlowId && newFlowId !== currentFlowId) {
      yield put(appActions.setFlowId(newFlowId))
    }

    success(desiredStep)
  } else if (cacheVerifySmsStatus === SmsVerifyResponse.Nok) {
    error()
  }
}

export function* setLoanParameters(): Saga {
  const a2345cid = localCookie.getCookie('a2345cid')
  const url = new URL(window.location.href)
  const loanData = yield {
    loanAmount: parseInt(url.searchParams.get('LOAN_AMOUNT') || '0', 10),
    rpsn: parseInt(url.searchParams.get('RPSN') || '0', 10),
    term: parseInt(url.searchParams.get('TERM') || '0', 10),
  }
  const client = {
    hashedCif: a2345cid,
    utmParams: JSON.stringify(getUtmParams(url)),
  }
  const request: any = {
    ...loanData,
    ...client,
  }

  yield put(clientActions.saveFetchedDataFromUrl(client))
  const response = yield call(getOpenApiInstance().initClientCache, request)

  if (response.responseStatus !== Response.ok) {
    throw new ResponseError('client setLoanParameters saga', response)
  }

  yield put(replace(Routes.CLIENT_INFO))

  if (loanData.loanAmount === 0 || loanData.term === 0 || loanData.rpsn === 0) {
    const isLocalhost = yield select(appSelectors.selectIsLocalhost)
    if (isLocalhost) {
      yield put(
        productActions.setDataPreConfigurator({
          instalmentDay: 15,
          loanAmount: 10000,
          rpsn: 4.3,
          term: 12,
        })
      )
    } else {
      yield put(modalDialogActions.showModalDialog(LOAN_DATA_MISSING))
    }
  } else {
    yield put(productActions.setDataPreConfigurator(loanData))
  }
}

export function* finishBranch({ payload }: Action<any>): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())
  const response = yield call(getOpenApiInstance().setupFinishOnBranch)

  const url = response.data?.url
  if (response.responseStatus === Response.ok && url) {
    yield openLinkNewTab(url)
  } else {
    yield put(
      modalDialogActions.showModalDialog(
        API_ERROR({
          errorInfo: { type: response.responseStatus },
          onRetry: payload,
        })
      )
    )
  }

  yield put(modalLoaderActions.hideModalLoader(loader.id))
}

export function* finishOnline(): Saga {
  const loader = yield put(modalLoaderActions.showModalLoader())
  yield call(getOpenApiInstance().finishOnline)
  yield put(modalLoaderActions.hideModalLoader(loader.id))
}
